import type { PropsWithChildren } from 'react';

import Swipe from 'react-easy-swipe';
import { Transition } from 'react-transition-group';

import Cross from 'public/cross.svg';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { FocusLockAndEscape } from 'src/app/components/PageSections/CardItemOverflowMenuWrapper/FocusLockAndEscape';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import LinkWithQuery from '../LinkWithQuery';

import {
  CloseButton,
  CloseButtonContainer,
  DecoratedLink,
  Logo,
  Menu,
  MenuButton,
  MenuButtonStyleLink,
  MenuHyperLink,
  MenuLink,
  Overlay,
  SectionHeader,
} from './styles';

export const SideMenuComponents = {
  SectionHeader,
  MenuLink,
  MenuHyperLink,
  DecoratedLink,
  MenuButton,
  MenuButtonStyleLink,
};

export interface SideMenuProps
  extends PropsWithChildren<{
    isShown: boolean;
    onClose: () => void;
    onLogoClick?: (e) => void;
    className?: string;
    logoTo?: string;
    hasGreenBackground?: boolean;
    isLogoPaddedOnLeftSide?: boolean;
    isHiddenOnLargeScreens?: boolean;
  }> {}

/**
 * This encapsulates a component which displays a side-menu that can slide out.
 *
 * State is managed externally.
 *
 * This component does not take care of anything but creating the menu, making sure it animates correctly,
 * and putting up an overlay that will trigger onClose when clicked.
 *
 * @param {bool} isShown Should this menu be shown or not?
 * @param {bool} hasGreenBackground Should the menu have a green background?
 * @param {bool} isLogoPaddedOnLeftSide Is the logo centered?
 * @param {bool} isHiddenOnLargeScreens Should the menu be hidden on large screens?
 * @param {function(e)} onLogoClick Action to handle logo being clicked
 * @param {function(e)} onClose An action to handle this menu being closed such as clicking on the overlay
 * @param (String?} className A className to be appended
 * @param {Node} children The children to render inside the menu
 * @return {Node}
 * @constructor
 */

const SideMenu = ({
  isShown,
  children,
  onClose,
  onLogoClick,
  className,
  hasGreenBackground,
  isLogoPaddedOnLeftSide,
  isHiddenOnLargeScreens,
  logoTo,
}: SideMenuProps) => {
  return (
    <FocusLockAndEscape handleEscapePressed={onClose}>
      <Swipe onSwipeLeft={onClose}>
        <div className={className}>
          <Overlay
            onClick={onClose}
            isHiddenOnLargeScreens={isHiddenOnLargeScreens}
            isShown={isShown}
            data-testid="overlay"
          />
          <Transition
            in={isShown}
            timeout={isShown ? 0 : 250}
            unmountOnExit
            mountOnEnter
            addEndListener={(node, done) => {
              node.addEventListener('transitionend', done, false);
            }}
          >
            {transitionState => (
              <Menu
                transitionState={transitionState}
                isHiddenOnLargeScreens={isHiddenOnLargeScreens}
                hasGreenBackground={hasGreenBackground}
                data-testid="menu"
                role="navigation"
                aria-label="Side Navigation Menu"
              >
                <CloseButtonContainer>
                  <button id="close-menu" type="button" aria-label="Close Menu" onClick={onClose}>
                    <CloseButton src={Cross} />
                  </button>
                </CloseButtonContainer>
                {logoTo ? (
                  <Logo isLogoPaddedOnLeftSide={isLogoPaddedOnLeftSide}>
                    <LinkWithQuery to={logoTo} onClick={onLogoClick}>
                      <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.capsule_logo} block />
                    </LinkWithQuery>
                  </Logo>
                ) : (
                  <Logo isLogoPaddedOnLeftSide={isLogoPaddedOnLeftSide}>
                    <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.capsule_logo} block />
                  </Logo>
                )}
                {children}
              </Menu>
            )}
          </Transition>
        </div>
      </Swipe>
    </FocusLockAndEscape>
  );
};

export default SideMenu;
